import React from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@components/ui/select';
import { AspectRatio, PhotoSize } from '../../types/photo';
import { SIZES } from '@acme/lib';
import { Card, CardContent } from '@/components/ui/card';
import { FaMountainSun } from 'react-icons/fa6';
import { cn } from '@lib/utils';
import { CheckIcon, XIcon } from 'lucide-react';

const sizeOptions = [
  {
    value: '1:1',
    label: '1:1',
    iconWidth: 'w-[24px]',
    iconHeight: 'h-[24px]',
    thumbnailWidth: 'w-[48px]',
    thumbnailHeight: 'h-[48px]',
    shape: 'square'
  },
  {
    value: '2:3',
    label: '2:3',
    dimensions: '512x768',
    iconWidth: 'w-[16px]',
    iconHeight: 'h-[24px]',
    thumbnailWidth: 'w-[32px]',
    thumbnailHeight: 'h-[48px]',
    shape: 'tall'
  },
  {
    value: '4:3',
    label: '4:3',
    iconWidth: 'w-[24px]',
    iconHeight: 'h-[18px]',
    thumbnailWidth: 'w-[48px]',
    thumbnailHeight: 'h-[36px]',
    shape: 'wide'
  }
];

type AspectRatioSelectorProps = {
  selectedAspectRatio: AspectRatio;
  onAspectRatioChange: (aspectRatio: AspectRatio) => void;
  modelVersion: '1.5' | 'flux.1-dev';
  selectedSize: PhotoSize;
  variant?: 'select' | 'radio';
};

export const AspectRatioSelector: React.FC<AspectRatioSelectorProps> = ({
  selectedAspectRatio,
  onAspectRatioChange,
  modelVersion,
  selectedSize = 'small',
  variant = 'select'
}) => {
  const aspectRatios = SIZES[modelVersion];

  if (variant === 'radio') {
    return (
      <div className="grid grid-cols-3 gap-4">
        {Object.keys(aspectRatios).map(aspectRatio => {
          const option = sizeOptions.find(opt => opt.value === aspectRatio);
          if (!option) return null;

          return (
            <Card
              key={aspectRatio}
              className={`shadow-none bg-background border-[1px] ${
                selectedAspectRatio === aspectRatio ? 'border-accent bg-accent/20' : 'border-border'
              } rounded-lg cursor-pointer transition-all duration-200 hover:border-accent`}
              onClick={() => onAspectRatioChange(aspectRatio as AspectRatio)}
            >
              <CardContent className="pb-0 p-4 flex flex-col items-center justify-center h-full relative">
                <div className="w-12 h-12 relative bg-foreground/10 ring-4 ring-foreground/10 flex items-center justify-center mb-2 rounded-sm">
                  {selectedAspectRatio === aspectRatio && (
                    <div className="absolute p-1 -bottom-3 -right-3 flex items-center justify-center bg-accent rounded-full z-10">
                      <CheckIcon strokeWidth={4} className="text-accent-foreground" size={12} />
                    </div>
                  )}
                  <div
                    className={`relative rounded-sm ${
                      selectedAspectRatio === aspectRatio ? 'bg-foreground' : 'bg-foreground/30'
                    } ${option.thumbnailWidth} ${option.thumbnailHeight}`}
                  >
                    <FaMountainSun
                      size={16}
                      className={cn('fill-background absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2')}
                    />
                  </div>
                </div>
                <div className="flex flex-col items-center justify-center gap-0 pt-2">
                  <div className="text-sm font-semibold">{aspectRatio}</div>
                  <div className="flex flex-row justify-center items-center text-[10px] md:text-xs text-muted-foreground font-mono">
                    {aspectRatios[aspectRatio as keyof typeof aspectRatios][selectedSize].width}
                    <span className="mx-1">
                      <XIcon size={10} />
                    </span>
                    {aspectRatios[aspectRatio as keyof typeof aspectRatios][selectedSize].height}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  }

  // Existing Select component logic
  return (
    <Select value={selectedAspectRatio} onValueChange={value => onAspectRatioChange(value as AspectRatio)}>
      <SelectTrigger className="">
        <SelectValue placeholder="Select Aspect Ratio" />
      </SelectTrigger>
      <SelectContent>
        {Object.keys(aspectRatios).map(aspectRatio => {
          const option = sizeOptions.find(opt => opt.value === aspectRatio);
          return (
            <SelectItem key={aspectRatio} value={aspectRatio} className="relative flex items-center">
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center">
                  <div className="w-6 h-6 flex items-center justify-center mr-3 bg-primary/10">
                    <div
                      className={`${option?.iconWidth} ${option?.iconHeight} border-primary/70 bg-primary/20 border-2 rounded-[2px]`}
                    ></div>
                  </div>
                  <span>{aspectRatio}</span>
                </div>
                <div className="text-primary/40 text-xs ml-3">
                  ({aspectRatios[aspectRatio as keyof typeof aspectRatios][selectedSize].width * 2}x
                  {aspectRatios[aspectRatio as keyof typeof aspectRatios][selectedSize].height * 2})
                </div>
              </div>
            </SelectItem>
          );
        })}
      </SelectContent>
    </Select>
  );
};

type SizeSelectorProps = {
  selectedAspectRatio: AspectRatio;
  selectedSize: PhotoSize;
  onSizeChange: (size: string, width: number, height: number) => void;
  modelVersion: '1.5' | 'flux.1-dev';
};

export const SizeSelector: React.FC<SizeSelectorProps> = ({
  selectedAspectRatio,
  selectedSize,
  onSizeChange,
  modelVersion
}) => {
  const currentSizes = SIZES[modelVersion];
  return (
    <Select
      value={selectedSize}
      onValueChange={value => {
        const newSize = value as PhotoSize;
        const sizes = SIZES[modelVersion];
        const { width, height } = sizes[selectedAspectRatio as keyof typeof sizes][newSize];
        onSizeChange(newSize, width, height);
      }}
    >
      <SelectTrigger className="">
        <SelectValue placeholder="Select Size" />
      </SelectTrigger>
      <SelectContent>
        {Object.entries(currentSizes[selectedAspectRatio as keyof typeof currentSizes]).map(([size, dimensions]) => (
          <SelectItem key={size} value={size} className="capitalize">
            <span className="capitalize">{`${size} `}</span>
            <span className="text-xs text-muted-foreground">{`(${dimensions.width}x${dimensions.height})`}</span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};
